/**
 * Main-column sections of the item editor (EditMediaItem): media, title & texts,
 * people & company, platform links. Render them as direct children of
 * <EditorMain> (hairlines between sections come from the stylesheet).
 */
import React, { useId } from 'react';
import { MDBCol, MDBIcon, MDBInput, MDBInputGroup, MDBRow, MDBTextArea } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import BilingualEditor from '../../../components-dev-admin/shared/BilingualEditor';
import { getLanguageText, mediaSlotOrder } from '../../../utils/mediathek';
import { CharCount, EditorSection, FieldGroup, LanguageSwitch } from '../MediathekEditorLayout';
import { ExternalVideoSlot, ImageSlot, MediaSlot, MediaSlotList, MediaStage } from '../MediaSlots';
import LinkedCompanyField from './LinkedCompanyField';

/**
 * Media: stage (what the website plays) + slot list ordered by series type.
 * @param {{item: object, seriesType?: string, uploads: {image: object, audio: object, video: object}, setField: Function, onImagePick: (file: File) => void, onMediaPick: (file: File, kind: 'audio'|'video') => void}} props
 */
export function ItemMediaSection({ item, seriesType, uploads, setField, onImagePick, onMediaPick }) {
  const { t } = useTranslation();
  const slots = {
    audio: (
      <MediaSlot
        kind="audio"
        file={item.audio}
        upload={uploads.audio}
        onPick={(file) => onMediaPick(file, 'audio')}
        onRemove={() => setField('audio', null)}
        hint={t('MP3, M4A, WAV, OGG or AAC, max. 300 MB')}
      />
    ),
    image: (
      <ImageSlot
        image={item.image}
        upload={uploads.image}
        onPick={onImagePick}
        onRemove={() => setField('image', null)}
        onAltChange={(alt) => setField('image', { ...item.image, alt })}
        hint={t('JPG or PNG, max. 3 MB. Without an image the website uses the YouTube thumbnail.')}
      />
    ),
    externalVideo: (
      <ExternalVideoSlot value={item.externalVideoUrl} onChange={(value) => setField('externalVideoUrl', value)} />
    ),
    video: (
      <MediaSlot
        kind="video"
        file={item.video}
        upload={uploads.video}
        onPick={(file) => onMediaPick(file, 'video')}
        onRemove={() => setField('video', null)}
        hint={t('MP4, WEBM, MOV or M4V, max. 2000 MB')}
      />
    ),
  };

  return (
    <EditorSection
      id="media"
      icon="photo-video"
      title={t('Media')}
      description={t('What the website plays and shows for this item.')}
    >
      {/* Stage and slots side by side (7/5) only when the main column itself is
          wide enough (container query in MediathekEditor.scss) - the app sidebar
          makes viewport breakpoints unreliable; stacked otherwise. */}
      <div className="me-media">
        <div className="me-media-grid">
          <MediaStage item={item} />
          <MediaSlotList label={t('Media')}>
            {mediaSlotOrder(seriesType).map((slot) => (
              <React.Fragment key={slot}>{slots[slot]}</React.Fragment>
            ))}
          </MediaSlotList>
        </div>
      </div>
    </EditorSection>
  );
}

/** True when an HTML string (Tiptap output) contains visible text or media. */
const hasRichText = (html) => {
  if (typeof html !== 'string' || !html) return false;
  if (/<(img|iframe|video|audio)[\s/>]/i.test(html)) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;/g, ' ').trim() !== '';
};

/**
 * Title & texts: one LanguageSwitch drives the title, the teaser AND the
 * description (BilingualEditor without its own tabs; both languages stay
 * mounted, remounted via `editorKey` whenever loaded / copied data arrives).
 * The filled dot marks a language that has any text (title, teaser or description).
 * @param {{item: object, lang: 'de'|'en', onLangChange: (lang: string) => void, titleInvalid: boolean, setLocalizedField: Function, editorKey: string}} props
 */
export function ItemTextsSection({ item, lang, onLangChange, titleInvalid, setLocalizedField, editorKey }) {
  const { t } = useTranslation();
  const uid = useId();
  const titleDe = getLanguageText(item.title, 'de');
  const titleEn = getLanguageText(item.title, 'en');
  const excerpt = getLanguageText(item.excerpt, lang);
  const isGerman = lang === 'de';
  const languageName = isGerman ? t('German') : t('English');
  const showTitleError = isGerman && titleInvalid;
  const titleErrorId = `${uid}-title-error`;
  const contentDe = getLanguageText(item.content, 'de');
  const contentEn = getLanguageText(item.content, 'en');
  const hasText = (code, title, content) => Boolean(
    title.trim() || getLanguageText(item.excerpt, code).trim() || hasRichText(content),
  );

  return (
    <EditorSection
      id="texts"
      icon="align-left"
      title={t('Title & texts')}
      tools={(
        <LanguageSwitch
          value={lang}
          onChange={onLangChange}
          filled={{ de: hasText('de', titleDe, contentDe), en: hasText('en', titleEn, contentEn) }}
          filledLabel={t('Text entered')}
          invalid={{ de: titleInvalid }}
        />
      )}
    >
      <div className="mb-4">
        <MDBInput
          key={`title-${lang}`}
          id={`${uid}-title-${lang}`}
          size="lg"
          label={isGerman ? `${t('Title German')} *` : t('Title English')}
          value={isGerman ? titleDe : titleEn}
          aria-required={isGerman || undefined}
          aria-invalid={showTitleError || undefined}
          aria-describedby={showTitleError ? titleErrorId : undefined}
          className={showTitleError ? 'is-invalid' : undefined}
          onChange={(e) => setLocalizedField('title', lang, e.target.value)}
        />
        {showTitleError && (
          <div className="me-error-text" id={titleErrorId}>{t('Please enter a German title')}</div>
        )}
      </div>

      <div className="mb-4">
        <MDBTextArea
          key={`excerpt-${lang}`}
          id={`${uid}-excerpt-${lang}`}
          label={`${t('Excerpt')} (${languageName})`}
          rows={3}
          value={excerpt}
          onChange={(e) => setLocalizedField('excerpt', lang, e.target.value)}
        />
        <CharCount text={excerpt} />
      </div>

      {/* BilingualEditor ends with mb-4; the section padding already spaces the bottom */}
      <div className="me-description" style={{ marginBottom: '-1.5rem' }}>
        <BilingualEditor
          key={editorKey}
          title="Description"
          language={lang}
          hideLanguageTabs
          contentDe={contentDe}
          contentEn={contentEn}
          onChangeDe={(html) => setLocalizedField('content', 'de', html)}
          onChangeEn={(html) => setLocalizedField('content', 'en', html)}
        />
      </div>
    </EditorSection>
  );
}

/**
 * People & company: guest / contact data, host, speakers; company name /
 * website and the optional linked company.
 * @param {{item: object, setField: Function, company: {linkedCompany: object|null, selectCompany: Function, clearCompany: Function, searchCompanies: Function}}} props
 */
export function ItemPeopleSection({ item, setField, company }) {
  const { t } = useTranslation();
  const uid = useId();
  return (
    <EditorSection id="people" icon="user-friends" title={t('People & company')}>
      <FieldGroup title={t('People')}>
        <MDBRow className="g-3">
          <MDBCol md="12" xxl="4">
            <MDBInput id={`${uid}-guest`} label={t('Guest / contact person')} value={item.guest} onChange={(e) => setField('guest', e.target.value)} />
          </MDBCol>
          <MDBCol md="6" xxl="4">
            <MDBInput id={`${uid}-guest-email`} label={t('Guest email')} type="email" autoComplete="off" value={item.guestEmail} onChange={(e) => setField('guestEmail', e.target.value)} />
          </MDBCol>
          <MDBCol md="6" xxl="4">
            <MDBInput id={`${uid}-guest-phone`} label={t('Guest phone')} type="tel" autoComplete="off" value={item.guestPhone} onChange={(e) => setField('guestPhone', e.target.value)} />
          </MDBCol>
          <MDBCol md="6" xxl="4">
            <MDBInput id={`${uid}-host`} label={t('Host / presenter')} value={item.host} onChange={(e) => setField('host', e.target.value)} />
          </MDBCol>
          <MDBCol md="6" xxl="8">
            <MDBInput id={`${uid}-speaker`} label={t('Speakers')} value={item.speaker} onChange={(e) => setField('speaker', e.target.value)} />
          </MDBCol>
        </MDBRow>
      </FieldGroup>

      <FieldGroup title={t('Company')}>
        <MDBRow className="g-3 mb-4">
          <MDBCol md="6">
            <MDBInput id={`${uid}-company-name`} label={t('Company name')} value={item.companyName} onChange={(e) => setField('companyName', e.target.value)} />
          </MDBCol>
          <MDBCol md="6">
            <MDBInput id={`${uid}-company-url`} label={t('Company website')} type="url" inputMode="url" value={item.companyUrl} onChange={(e) => setField('companyUrl', e.target.value)} />
          </MDBCol>
        </MDBRow>
        <LinkedCompanyField
          companyId={item.companyId}
          linkedCompany={company.linkedCompany}
          selectCompany={company.selectCompany}
          clearCompany={company.clearCompany}
          searchCompanies={company.searchCompanies}
        />
      </FieldGroup>
    </EditorSection>
  );
}

/** Platform links (Spotify, Podigee) with brand icon prefixes. */
export function ItemLinksSection({ item, setField }) {
  const { t } = useTranslation();
  const uid = useId();
  return (
    <EditorSection id="links" icon="link" title={t('Platform links')}>
      <MDBRow className="g-3">
        <MDBCol md="6">
          <MDBInputGroup textBefore={<MDBIcon fab icon="spotify" aria-hidden="true" />}>
            <MDBInput id={`${uid}-spotify`} label={t('Spotify URL')} type="url" inputMode="url" value={item.spotifyUrl} onChange={(e) => setField('spotifyUrl', e.target.value)} />
          </MDBInputGroup>
        </MDBCol>
        <MDBCol md="6">
          <MDBInputGroup textBefore={<MDBIcon fas icon="podcast" aria-hidden="true" />}>
            <MDBInput id={`${uid}-podigee`} label={t('Podigee URL')} type="url" inputMode="url" value={item.podigeeUrl} onChange={(e) => setField('podigeeUrl', e.target.value)} />
          </MDBInputGroup>
        </MDBCol>
      </MDBRow>
    </EditorSection>
  );
}
